// src/stores/groupWalkieStore.js
// Group Walkie in the web app (docs/features/native_comms_suite_FEATURE.md Phase 4): the group chat's call icon, the
// ring when a teammate starts a call in one of your groups, and the call itself (GroupWalkiePanel plays the audio).
// Behind frontendFlags.walkieGroupCalls, flipped with globalSettings/environmentControls.nativeStaffWalkie_enabled
// (the server's gate on the walkieGroupRoom callable).
import { defineStore } from 'pinia'
import { ref, shallowRef, computed } from 'vue'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/services/firebaseconfig'
import { groupWalkieService, MAX_LIVE } from '@/services/GroupWalkieService'
import { useFrontendFlagsStore } from '@/stores/frontendFlagsStore'
import { useWalkieStore } from '@/stores/walkieStore'
import { useGroupChatStore } from '@/stores/groupChatStore'
import { eventBus } from '@/utils/eventBus'
import { getInstanceByUid } from '@/utils/aiInstances'
import { playRingtone, playConnectTone, playDisconnectTone } from '@/utils/audioTones'

const INVITE_RING_MS = 30 * 1000

export const useGroupWalkieStore = defineStore('groupWalkie', () => {
  const myUid = ref(null)
  const call = ref(null) // { groupId, roomId, groupName, live: [uid] }
  const joining = ref(false)
  const muted = ref(false)
  const streams = shallowRef({}) // uid -> MediaStream
  const levels = ref({}) // uid -> 0..1
  const names = ref({}) // uid -> display name
  const invite = ref(null) // { groupId, roomId, fromUid, fromName, groupName }
  let stopRing = null
  let inviteTimer = null

  const flags = useFrontendFlagsStore()
  const enabled = computed(() => flags.isFlagOn('walkieGroupCalls'))
  const inCall = computed(() => Boolean(call.value))
  const talking = computed(() => Object.keys(levels.value).filter(uid => levels.value[uid] > 0.04))

  function initialize(uid, ownership) {
    if (myUid.value === uid) return
    // An AI account's tab is not its phone (walkieStore does the same): no listening, no ringing.
    if (getInstanceByUid(uid)) return
    myUid.value = uid
    groupWalkieService.setAudioOwnership(ownership)
    groupWalkieService.onInvite = handleInvite
    groupWalkieService.onRoom = handleRoom
    groupWalkieService.onStream = (peer, stream) => {
      const next = { ...streams.value }
      if (stream) next[peer] = stream
      else delete next[peer]
      streams.value = next
    }
    groupWalkieService.onLevels = value => {
      levels.value = value
    }
    groupWalkieService.onEnded = reason => {
      const was = call.value
      reset()
      if (was) playDisconnectTone()
      if (reason === 'dropped')
        eventBus.emit('toast', { type: 'error', message: 'You were dropped from the Walkie call.' })
    }
    groupWalkieService.onError = error => console.warn('[GroupWalkie]', error)
    groupWalkieService.startListening(uid)
  }

  async function cleanup() {
    dismissInvite()
    await groupWalkieService.stopListening()
    reset()
    myUid.value = null
  }

  function reset() {
    call.value = null
    joining.value = false
    muted.value = false
    streams.value = {}
    levels.value = {}
  }

  async function groupName(groupId) {
    const known = useGroupChatStore().groups.find(g => g.groupId === groupId)
    if (known?.name) return known.name
    try {
      const snap = await getDoc(doc(db, 'groupChats', groupId))
      return snap.exists() ? snap.data().name || 'Group' : 'Group'
    } catch {
      return 'Group'
    }
  }

  async function nameMembers(uids) {
    const missing = uids.filter(uid => !names.value[uid])
    if (!missing.length) return
    const details = await useGroupChatStore().getMemberDetails(missing)
    const next = { ...names.value }
    for (const member of details) next[member.uid] = member.displayName || 'Teammate'
    names.value = next
  }

  /** The group chat's call icon: start a call, or join the one going (roomId). */
  async function enter(groupId, roomId = null) {
    if (!enabled.value || joining.value || call.value) return false
    if (useWalkieStore().isInCall) {
      eventBus.emit('toast', { type: 'error', message: 'Finish your Walkie call first.' })
      return false
    }
    joining.value = true
    dismissInvite()
    try {
      const view = await groupWalkieService.enter(groupId, roomId)
      call.value = {
        groupId,
        roomId: view.roomId,
        groupName: await groupName(groupId),
        live: view.live,
      }
      nameMembers(view.live)
      playConnectTone()
      return true
    } catch (error) {
      eventBus.emit('toast', {
        type: 'error',
        message: error?.message || 'The Walkie call could not start.',
      })
      return false
    } finally {
      joining.value = false
    }
  }

  async function leave() {
    await groupWalkieService.leave()
  }

  function toggleMute() {
    muted.value = !muted.value
    groupWalkieService.setMuted(muted.value)
  }

  function handleRoom({ groupId, roomId, live }) {
    if (!call.value || call.value.roomId !== roomId) return
    call.value = { ...call.value, groupId, live }
    nameMembers(live)
  }

  async function handleInvite({ groupId, roomId, fromUid }) {
    if (!enabled.value || call.value || invite.value?.roomId === roomId) return
    const [group] = await Promise.all([groupName(groupId), nameMembers([fromUid])])
    invite.value = {
      groupId,
      roomId,
      fromUid,
      fromName: names.value[fromUid] || 'A teammate',
      groupName: group,
    }
    // A 1:1 call is up: show the invite without ringing over it.
    if (!useWalkieStore().isInCall) stopRing = playRingtone()
    clearTimeout(inviteTimer)
    inviteTimer = setTimeout(stopRinging, INVITE_RING_MS)
  }

  function stopRinging() {
    if (stopRing) stopRing()
    stopRing = null
  }

  function dismissInvite() {
    stopRinging()
    clearTimeout(inviteTimer)
    inviteTimer = null
    invite.value = null
  }

  return {
    MAX_LIVE,
    myUid,
    call,
    joining,
    muted,
    streams,
    levels,
    names,
    invite,
    enabled,
    inCall,
    talking,
    initialize,
    cleanup,
    enter,
    leave,
    toggleMute,
    dismissInvite,
  }
})
